import { createElement, lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react';
import type { EntityType } from '@shared/types';
import type { IconName } from '@/ui/icons';
import type { RecordRef, RecordType, ViewId } from './router';

// How the product areas plug into the shell and into each other without importing one another:
// - views: the screen behind each main navigation entry
// - slots: a piece of one area shown inside another (the Deals box on a record, comments on a deal)
// - admin sections: pages of the admin console
// - globals: components mounted once for the whole session (the composer)
// - commands: entries in the search palette
// - navigation dots: the small dot on a main navigation entry (new replies on Inbox)
// Each area registers its parts from its setup() in src/client/areas, before the app first renders.

/* ---------- views ---------- */

type ViewLoader = () => Promise<{ default: ComponentType }>;
const viewLoaders = new Map<ViewId, ViewLoader>();
const viewComponents = new Map<ViewId, LazyExoticComponent<ComponentType>>();

/** Registers the screen for a view. The code loads on first visit, or earlier while the browser is idle. */
export function registerView(id: ViewId, load: ViewLoader): void {
  viewLoaders.set(id, load);
  viewComponents.set(id, lazy(load));
}

export function viewComponent(id: ViewId): LazyExoticComponent<ComponentType> | undefined {
  return viewComponents.get(id);
}

/** Fetches every view's code in the background, so switching views later never waits on the network. */
export function preloadViews(): void {
  for (const load of viewLoaders.values()) void load().catch(() => undefined);
}

/* ---------- slots ---------- */

export interface SlotProps {
  /** The content of an open person or company record sheet. Record area. */
  'record.sheet': { record: RecordRef };
  /** The admin console, showing one section. Admin area. */
  'admin.console': { section: string };
  /**
   * The Deals box on a record: a person's deals plus their company's, or a company's deals. `focus` is a deal id
   * to expand, scroll into view and highlight, with its comments open. Deals area.
   */
  'record.deals': { type: RecordType; id: string; /** The record's display name, for labels. */ recordName: string; focus?: string };
  /** The AI review on a company or person: the values the AI proposed for its details, each to accept or reject. Finder area. */
  'record.fill': { type: 'company' | 'person'; id: string };
  /** Under a company's people: finding its key people with AI, and the people found, each to add or dismiss. Finder area. */
  'record.people': { companyId: string; companyName: string };
  /** Internal comments with @mentions on a person, company or deal: the thread plus a box to write one. Record area. */
  comments: { entityType: EntityType; entityId: string; /** A tighter layout for use inside a deal. */ compact?: boolean };
  /** Your Gmail inbox and calendar connection, shown in your account settings. Gmail area. */
  'account.inbox': Record<never, never>;
}

export type SlotName = keyof SlotProps;
const slots = new Map<SlotName, ComponentType<never>>();

export function registerSlot<K extends SlotName>(name: K, component: ComponentType<SlotProps[K]>): void {
  slots.set(name, component as ComponentType<never>);
}

export function hasSlot(name: SlotName): boolean {
  return slots.has(name);
}

/** Renders whatever an area registered for the slot, or `fallback` when nothing is registered. */
export function Slot<K extends SlotName>({ name, fallback = null, ...props }: { name: K; fallback?: ReactNode } & SlotProps[K]): ReactNode {
  const component = slots.get(name) as ComponentType<SlotProps[K]> | undefined;
  if (!component) return fallback;
  return createElement(component, props as unknown as SlotProps[K]);
}

/* ---------- admin console sections ---------- */

export type AdminGroup = 'Workspace' | 'Data' | 'AI & safety';
export const ADMIN_GROUPS: AdminGroup[] = ['Workspace', 'Data', 'AI & safety'];

export interface AdminSection {
  /** Used in the address bar (?admin=<id>). */
  id: string;
  label: string;
  icon: IconName;
  group: AdminGroup;
  /** Position within the group, lowest first. */
  order: number;
  component: ComponentType;
  /** Returns true when the section needs the admin's attention; shows a small dot in the navigation. A hook. */
  useAttention?: () => boolean;
}

const adminSections = new Map<string, AdminSection>();

export function registerAdminSection(section: AdminSection): void {
  adminSections.set(section.id, section);
}

/** Every registered section, grouped and ordered as the console navigation shows them. */
export function adminSectionList(): AdminSection[] {
  return [...adminSections.values()].sort(
    (a, b) => ADMIN_GROUPS.indexOf(a.group) - ADMIN_GROUPS.indexOf(b.group) || a.order - b.order || a.label.localeCompare(b.label),
  );
}

export function adminSection(id: string): AdminSection | undefined {
  return adminSections.get(id);
}

/* ---------- always-mounted pieces ---------- */

const globals = new Map<string, ComponentType>();

/** Mounts a component once for the signed-in session, such as the composer or a live listener. */
export function registerGlobal(key: string, component: ComponentType): void {
  globals.set(key, component);
}

export function globalComponents(): [string, ComponentType][] {
  return [...globals.entries()];
}

/* ---------- search palette commands ---------- */

export interface PaletteCommand {
  id: string;
  title: string;
  sub?: string;
  icon: IconName;
  /** A shortcut shown on the right, such as "G P" or "E". */
  keys?: string;
  /** Extra words the search matches on. */
  keywords?: string;
  run: () => void;
  /** Lists the command under "Jump to" when the search box is empty. */
  jump?: boolean;
  /**
   * Sort order, lowest first. The shell's own commands use 10-90 (going to each view, the bin, the admin console)
   * and 150-190 (notifications, account, shortcuts, sign out). Areas slot in between: writing an email 100,
   * replying 110, sending to a list 120, a new AI search 130, backing up 140.
   */
  order?: number;
}

/** A hook returning commands; it may read data, so commands can name real lists and conversations. */
export type CommandSource = () => PaletteCommand[];
const commandSources: CommandSource[] = [];

export function registerCommands(source: CommandSource): void {
  commandSources.push(source);
}

/** The registered sources, in a fixed order (they are hooks, so the list never changes after start-up). */
export function commandSourceList(): readonly CommandSource[] {
  return commandSources;
}

/* ---------- navigation dots ---------- */

/** A hook returning a short reason when the view's navigation entry should show a dot (such as "New replies"), or null. */
export type NavDotSource = () => string | null;
const navDots = new Map<ViewId, NavDotSource>();

/** Shows a small dot on a view's navigation entry while the hook returns a reason. One per view. */
export function registerNavDot(view: ViewId, source: NavDotSource): void {
  navDots.set(view, source);
}

export function navDotSource(view: ViewId): NavDotSource | undefined {
  return navDots.get(view);
}
